<template>
  <div class="tree-item-wrapper" :style="indentationStyle">
    <div
      class="tree-item-content"
      :class="{
        'has-children': !isLeaf,
        'has-unsaved-changes': hasUnsavedChanges,
      }"
    >
      <!-- Toggle button for expandable items -->
      <v-btn
        v-if="!isLeaf"
        :icon="isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right'"
        variant="text"
        size="x-small"
        class="expand-btn"
        @click="toggleExpanded"
      />
      <div v-else class="expand-spacer"></div>

      <!-- Key label -->
      <span class="key-label">{{ keyPath.split('.').pop() }}:</span>

      <!-- Value input for leaf nodes -->
      <div v-if="isLeaf" class="value-input">
        <v-text-field
          v-if="typeof value === 'string'"
          :model-value="value"
          @update:model-value="updateValue"
          variant="underlined"
          density="compact"
          class="value-field"
          hide-details
        />
        <v-text-field
          v-else-if="typeof value === 'number'"
          :model-value="value"
          @update:model-value="updateValue"
          type="number"
          variant="underlined"
          density="compact"
          class="value-field"
          hide-details
        />
        <v-checkbox
          v-else-if="typeof value === 'boolean'"
          :model-value="value"
          @update:model-value="updateValue"
          class="value-field"
          hide-details
        />
        <span v-else class="value-display">{{ value }}</span>
      </div>
    </div>

    <!-- Children for expanded non-leaf nodes -->
    <div v-if="!isLeaf && isExpanded" class="tree-children">
      <!-- Special handling for tour step objects -->
      <template v-if="isTourStepObject">
        <div class="tour-step-fields">
          <div class="tree-item-wrapper" :style="childIndentationStyle">
            <div
              class="tree-item-content"
              :class="{
                'has-unsaved-changes': hasTourStepFieldChanged('title'),
              }"
            >
              <div class="expand-spacer"></div>
              <span class="key-label">title:</span>
              <div class="value-input">
                <v-text-field
                  :model-value="value.title"
                  @update:model-value="
                    (newValue) => updateTourStepField('title', newValue)
                  "
                  variant="underlined"
                  density="compact"
                  class="value-field"
                  hide-details
                />
              </div>
            </div>
          </div>
          <div class="tree-item-wrapper" :style="childIndentationStyle">
            <div
              class="tree-item-content"
              :class="{
                'has-unsaved-changes': hasTourStepFieldChanged('content'),
              }"
            >
              <div class="expand-spacer"></div>
              <span class="key-label">content:</span>
              <div class="value-input">
                <v-textarea
                  :model-value="value.content"
                  @update:model-value="
                    (newValue) => updateTourStepField('content', newValue)
                  "
                  variant="underlined"
                  density="compact"
                  class="value-field"
                  hide-details
                  rows="3"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- Regular tree handling for other objects -->
      <template v-else>
        <TranslationTreeItem
          v-for="(childValue, childKey) in value"
          :key="childKey"
          :key-path="keyPath + '.' + childKey"
          :value="childValue"
          :force-expanded="forceExpanded"
          :depth="depth + 1"
          :has-changes="hasChanges"
          :original-value="getChildOriginalValue(childKey)"
          @update="(keyPath, newValue) => $emit('update', keyPath, newValue)"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue'

  const props = defineProps({
    keyPath: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number, Boolean, Object, Array],
      required: true,
    },
    forceExpanded: {
      type: Boolean,
      default: false,
    },
    depth: {
      type: Number,
      default: 0,
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    originalValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
  })

  const emit = defineEmits(['update'])

  const isExpanded = ref(false)

  // Debug: Log the keyPath when component is created
  console.log(
    `[TranslationTreeItem] Component created with keyPath: ${props.keyPath}`
  )

  const isLeaf = computed(() => {
    if (typeof props.value !== 'object' || props.value === null) {
      return true
    }

    // Special handling for tour step objects - treat them as non-leaf but with special rendering
    if (isTourStepObject.value) {
      return false
    }

    return false
  })

  const isTourStepObject = computed(() => {
    return (
      typeof props.value === 'object' &&
      props.value !== null &&
      Object.prototype.hasOwnProperty.call(props.value, 'title') &&
      Object.prototype.hasOwnProperty.call(props.value, 'content')
    )
  })

  const indentationStyle = computed(() => {
    return {
      paddingLeft: `${props.depth * 16}px`,
    }
  })

  const childIndentationStyle = computed(() => {
    return {
      paddingLeft: `${(props.depth + 1) * 16}px`,
    }
  })

  const hasUnsavedChanges = computed(() => {
    if (!props.hasChanges || !props.originalValue) return false

    // Only highlight leaf nodes (actual input fields), not parent containers
    if (!isLeaf.value) return false

    return JSON.stringify(props.value) !== JSON.stringify(props.originalValue)
  })

  // Helper function to check if a tour step field has changed
  const hasTourStepFieldChanged = (fieldName) => {
    if (!props.hasChanges || !props.originalValue || !isTourStepObject.value)
      return false

    const currentValue = props.value[fieldName]
    const originalValue = props.originalValue[fieldName]
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue)
  }

  // Helper function to get original value for child paths
  const getChildOriginalValue = (childKey) => {
    if (!props.originalValue || typeof props.originalValue !== 'object')
      return null
    return props.originalValue[childKey] || null
  }

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value
  }

  const updateValue = (newValue) => {
    console.log(
      `[TranslationTreeItem] updateValue called for keyPath: ${props.keyPath}, newValue:`,
      newValue
    )
    emit('update', props.keyPath, newValue)
  }

  const updateTourStepField = (field, newValue) => {
    const updatedStep = { ...props.value, [field]: newValue }
    emit('update', props.keyPath, updatedStep)
  }

  // Watch for forceExpanded changes
  watch(
    () => props.forceExpanded,
    (newValue) => {
      if (!isLeaf.value) {
        isExpanded.value = newValue
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
  .tree-item-wrapper {
    margin-bottom: 4px;
  }

  .tree-item-content {
    display: flex;
    align-items: center;
    padding: 4px 0;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    width: 100%;
    min-width: 0;
  }

  .tree-item-content:hover {
    background-color: #f5f5f5;
  }

  .tree-item-content.has-children {
    font-weight: 500;
    color: #1976d2;
  }

  /* Highlighting for unsaved changes */
  .tree-item-content.has-unsaved-changes {
    background-color: #fff3cd !important;
    border-left: 3px solid #ffc107;
    padding-left: 8px;
    margin-left: -8px;
    border-radius: 4px;
  }

  .tree-item-content.has-unsaved-changes .key-label {
    color: #856404;
    font-weight: 600;
  }

  .tree-item-content.has-unsaved-changes .value-field :deep(.v-field__outline) {
    border-color: #ffc107 !important;
  }

  .tree-item-content.has-unsaved-changes
    .value-field
    :deep(.v-field__outline--active) {
    border-color: #ffc107 !important;
  }

  .tree-item-content.has-unsaved-changes .value-field :deep(.v-field__input) {
    background-color: #fff3cd !important;
    color: #856404 !important;
    font-weight: 600;
  }

  .tree-item-content.has-unsaved-changes .value-field :deep(.v-field__field) {
    background-color: #fff3cd !important;
  }

  .expand-btn {
    margin-right: 8px;
    min-width: 24px;
  }

  .expand-spacer {
    width: 24px;
    margin-right: 8px;
  }

  .key-label {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #424242;
    margin-right: 16px;
  }

  .value-input {
    flex: 1 1 100% !important;
    max-width: 400px;
    width: 100% !important;
    min-width: 0;
    flex-basis: 100% !important;
  }

  .value-field {
    font-size: 14px;
    width: 100% !important;
  }

  /* Ensure inputs maintain full width when focused - Vuetify 3 specific */
  .value-field :deep(.v-field__input) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field__field) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-input__control) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-text-field__input) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  /* Vuetify 3 specific classes */
  .value-field :deep(.v-field__overlay) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field__outline) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field__underlay) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  /* Prevent focus-related width changes */
  .value-field:focus-within {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field--focused) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field--focused .v-field__input) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  /* Additional Vuetify 3 focus states */
  .value-field :deep(.v-field--focused .v-field__field) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  .value-field :deep(.v-field--focused .v-field__overlay) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  /* Force all input elements to maintain width */
  .value-field :deep(input) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  .value-field :deep(textarea) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  .tree-children {
    margin-top: 4px;
  }

  .tour-step-fields {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 6px;
    margin: 2px 0;
  }

  .value-display {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #666;
    padding: 8px;
    background-color: #f5f5f5;
    border-radius: 4px;
  }
</style>
